import { PageSkeleton } from '@euroasiann/shared/ui';
/**
 * Organization Profile Page
 * Shows detailed information about an organization including:
 * - Users and their roles (filtered to this organization only)
 * - License information
 * - Vessels
 * - Business Units
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '../../components/shared/DataTable';
import { useToast } from '../../components/shared/Toast';
import { MdArrowBack, MdPeople, MdVpnKey, MdDirectionsBoat, MdBusiness, MdBusinessCenter, MdHistory, MdSend, MdCategory, MdInventory2 } from 'react-icons/md';
import { cn } from '../../lib/utils';

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

interface Organization {
  _id: string;
  name: string;
  type: string;
  portalType: string;
  isActive: boolean;
  createdAt?: string;
}

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  lastLogin?: string;
  organizationId?: string; // <-- Added: links user to organization
  // If your backend uses nested object, use this instead:
  // organization?: { _id: string; name?: string };
}

interface License {
  _id: string;
  status: string;
  expiresAt?: string;
  issuedAt?: string;
  usageLimits?: {
    users?: number;
    vessels?: number;
    items?: number;
    employees?: number;
    businessUnits?: number;
  };
  currentUsage?: {
    users?: number;
    vessels?: number;
    items?: number;
    employees?: number;
    businessUnits?: number;
  };
}

interface Vessel {
  _id: string;
  name: string;
  imoNumber?: string;
  status?: string;
}

interface BusinessUnit {
  _id: string;
  name: string;
  code?: string;
  status?: string;
}

interface VendorCatalogItem {
  _id: string;
  impa?: string;
  description?: string;
  partNo?: string;
  brand?: string;
  model?: string;
  category?: string;
  price?: number;
  currency?: string;
  availableQty?: number | null;
  currentStock?: number | null;
  availability?: 'in-stock' | 'limited' | 'out-of-stock' | 'not-tracked';
  stockStatus?: string;
}

interface VendorInventoryItem {
  _id: string;
  impa?: string;
  description?: string;
  partNo?: string;
  brand?: string;
  model?: string;
  price?: number;
  currency?: string;
  currentStock?: number;
  reservedStock?: number;
  availableStock?: number;
  isLowStock?: boolean;
  status?: string;
  warehouseLocation?: string;
}

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    'Content-Type': 'application/json',
  };
}

function formatMoney(price?: number, currency?: string) {
  if (price == null || Number.isNaN(price)) return '—';
  const code = currency?.trim() || 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(price);
  } catch {
    return `${code} ${price.toFixed(2)}`;
  }
}

function availabilityBadgeClass(availability?: string) {
  switch (availability) {
    case 'in-stock':
      return 'bg-emerald-100 text-[hsl(var(--foreground))] font-semibold dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800';
    case 'limited':
      return 'bg-amber-100 text-[hsl(var(--foreground))] font-semibold dark:bg-amber-900/50 ring-1 ring-amber-200 dark:ring-amber-800';
    case 'out-of-stock':
      return 'bg-red-100 text-[hsl(var(--foreground))] font-semibold dark:bg-red-900/50 ring-1 ring-red-200 dark:ring-red-800';
    default:
      return 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] ring-1 ring-[hsl(var(--border))]';
  }
}

function availabilityLabel(availability?: string) {
  switch (availability) {
    case 'in-stock':
      return 'In stock';
    case 'limited':
      return 'Limited';
    case 'out-of-stock':
      return 'Out of stock';
    case 'not-tracked':
      return 'Not tracked';
    default:
      return availability || '—';
  }
}

export function OrganizationProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<
    'overview' | 'users' | 'license' | 'vessels' | 'business-units' | 'catalog' | 'inventory' | 'logs'
  >('overview');

  // Fetch organization details
  const { data: organization, isLoading: orgLoading } = useQuery<Organization>({
    queryKey: ['organization', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch organization');
      const data = await response.json();
      return data.data;
    },
    enabled: !!id,
  });

  // Fetch users for this organization
  const { data: users, isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['organization-users', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${id}/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch users');
        return [];
      }

      const result = await response.json();
      return Array.isArray(result.data) ? result.data : [];
    },
    enabled: !!id,
  });

  // Fetch license for this organization
  const { data: license, isLoading: licenseLoading } = useQuery<License>({
    queryKey: ['organization-license', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${id}/license`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.data || null;
    },
    enabled: !!id,
  });

  // Fetch vessels (for customer organizations)
  const { data: vessels, isLoading: vesselsLoading } = useQuery<Vessel[]>({
    queryKey: ['organization-vessels', id],
    queryFn: async () => {
      if (organization?.type !== 'customer') return [];
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${id}/vessels`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch vessels');
      const data = await response.json();
      // Handle potential pagination
      const raw = data.data?.vessels ?? data.data;
      return data.success ? (Array.isArray(raw) ? raw : []) : [];
    },
    enabled: !!id && organization?.type === 'customer',
  });

  // Fetch business units (for customer organizations)
  const { data: businessUnits, isLoading: busLoading } = useQuery<BusinessUnit[]>({
    queryKey: ['organization-business-units', id],
    queryFn: async () => {
      if (organization?.type !== 'customer') return [];
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${id}/business-units`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch business units');
      const data = await response.json();
      const raw = data.data?.businessUnits ?? data.data;
      return data.success ? (Array.isArray(raw) ? raw : []) : [];
    },
    enabled: !!id && organization?.type === 'customer',
  });

  // Fetch activity logs for this organization
  const { data: logsResult, isLoading: logsLoading } = useQuery<{ logs: any[], total: number }>({
    queryKey: ['organization-logs', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/activity-logs?organizationId=${id}&limit=100`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch activity logs');
      const data = await response.json();
      return data.data;
    },
    enabled: !!id && activeTab === 'logs',
  });

  const isVendorOrgQuery = !!organization && organization.type !== 'customer';

  const { data: vendorCatalog, isLoading: catalogLoading } = useQuery<VendorCatalogItem[]>({
    queryKey: ['organization-vendor-catalog', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${id}/catalog?limit=1000`, {
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch vendor catalog');
      const data = await response.json();
      return data.success && Array.isArray(data.data) ? data.data : [];
    },
    enabled: !!id && isVendorOrgQuery,
  });

  const { data: vendorInventoryResult, isLoading: inventoryLoading } = useQuery<{
    items: VendorInventoryItem[];
    total: number;
  }>({
    queryKey: ['organization-vendor-inventory', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${id}/inventory?limit=1000`, {
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch vendor inventory');
      const data = await response.json();
      const payload = data.data;
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const total = typeof payload?.total === 'number' ? payload.total : items.length;
      return { items, total };
    },
    enabled: !!id && isVendorOrgQuery,
  });

  const vendorInventory = vendorInventoryResult?.items ?? [];

  if (orgLoading) {
    return <PageSkeleton variant="cards" />;
  }

  if (!organization) {
    return (
      <div className="p-12 text-center">
        <p className="text-[hsl(var(--destructive))]">Organization not found</p>
        <button onClick={() => navigate('/organizations')} className="mt-4 px-4 py-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg">
          Back to Organizations
        </button>
      </div>
    );
  }

  const isVendorOrg = organization.type !== 'customer';
  const vendorOrganizationId = organization._id ? String(organization._id) : '';
  const canSendRfq = isVendorOrg && !!vendorOrganizationId && organization.isActive;

  const handleSendRfqToVendor = () => {
    if (!vendorOrganizationId) {
      showToast('Organization ID is missing.', 'error');
      return;
    }
    if (!organization.isActive) {
      showToast('Activate this vendor before sending an RFQ.', 'warning');
      return;
    }
    const params = new URLSearchParams({
      vendorId: vendorOrganizationId,
      vendorName: organization.name || 'Vendor',
    });
    navigate(`/dashboard/admin/rfqs/new?${params.toString()}`);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: MdBusinessCenter },
    { id: 'users', label: 'Users', icon: MdPeople },
    { id: 'license', label: 'License', icon: MdVpnKey },
    ...(organization.type === 'customer'
      ? [
          { id: 'vessels', label: 'Vessels', icon: MdDirectionsBoat },
          { id: 'business-units', label: 'Business Units', icon: MdBusiness },
        ]
      : [
          { id: 'catalog', label: 'Catalog', icon: MdCategory },
          { id: 'inventory', label: 'Inventory', icon: MdInventory2 },
        ]),
    { id: 'logs', label: 'Activity Logs', icon: MdHistory },
  ];

  const catalogColumns = [
    {
      key: 'partNo',
      header: 'Part / IMPA',
      render: (item: VendorCatalogItem) => (
        <div>
          <div className="font-semibold text-[hsl(var(--foreground))]">{item.partNo || item.impa || '—'}</div>
          {item.partNo && item.impa && (
            <div className="text-xs text-[hsl(var(--muted-foreground))]">IMPA {item.impa}</div>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (item: VendorCatalogItem) => (
        <span className="text-sm text-[hsl(var(--muted-foreground))] max-w-xs truncate block" title={item.description}>
          {item.description || '—'}
        </span>
      ),
    },
    {
      key: 'brand',
      header: 'Brand',
      render: (item: VendorCatalogItem) => <span>{item.brand || '—'}</span>,
    },
    {
      key: 'model',
      header: 'Model',
      render: (item: VendorCatalogItem) => <span>{item.model || '—'}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      render: (item: VendorCatalogItem) => <span>{item.category || '—'}</span>,
    },
    {
      key: 'stock',
      header: 'Stock',
      render: (item: VendorCatalogItem) => (
        <div className="space-y-1">
          <span className="text-sm font-medium text-[hsl(var(--foreground))]">
            {item.availableQty != null ? item.availableQty : item.currentStock != null ? item.currentStock : '—'}
          </span>
          <span
            className={cn(
              'px-2 py-0.5 inline-flex text-[10px] leading-4 font-bold rounded-full',
              availabilityBadgeClass(item.availability)
            )}
          >
            {availabilityLabel(item.availability)}
          </span>
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      render: (item: VendorCatalogItem) => (
        <span className="font-medium">{formatMoney(item.price, item.currency)}</span>
      ),
    },
  ];

  const inventoryColumns = [
    {
      key: 'partNo',
      header: 'Part / IMPA',
      render: (item: VendorInventoryItem) => (
        <div>
          <div className="font-semibold text-[hsl(var(--foreground))]">{item.partNo || item.impa || '—'}</div>
          {item.partNo && item.impa && (
            <div className="text-xs text-[hsl(var(--muted-foreground))]">IMPA {item.impa}</div>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (item: VendorInventoryItem) => (
        <span className="text-sm text-[hsl(var(--muted-foreground))] max-w-xs truncate block" title={item.description}>
          {item.description || '—'}
        </span>
      ),
    },
    {
      key: 'brand',
      header: 'Brand',
      render: (item: VendorInventoryItem) => <span>{item.brand || '—'}</span>,
    },
    {
      key: 'model',
      header: 'Model',
      render: (item: VendorInventoryItem) => <span>{item.model || '—'}</span>,
    },
    {
      key: 'availableStock',
      header: 'Available',
      render: (item: VendorInventoryItem) => (
        <span className="font-semibold text-[hsl(var(--foreground))]">{item.availableStock ?? '—'}</span>
      ),
    },
    {
      key: 'currentStock',
      header: 'On hand / Reserved',
      render: (item: VendorInventoryItem) => (
        <span className="text-sm text-[hsl(var(--muted-foreground))]">
          {item.currentStock ?? 0} / {item.reservedStock ?? 0}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: VendorInventoryItem) => (
        <span
          className={cn(
            'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
            item.isLowStock
              ? 'bg-amber-100 text-[hsl(var(--foreground))] font-semibold dark:bg-amber-900/50 ring-1 ring-amber-200 dark:ring-amber-800'
              : 'bg-emerald-100 text-[hsl(var(--foreground))] font-semibold dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800'
          )}
        >
          {item.isLowStock ? 'Low stock' : item.status || 'Active'}
        </span>
      ),
    },
    {
      key: 'price',
      header: 'List price',
      render: (item: VendorInventoryItem) => (
        <span className="font-medium">{formatMoney(item.price, item.currency)}</span>
      ),
    },
  ];

  const userColumns = [
    {
      key: 'name',
      header: 'Name',
      render: (user: User) => (
        <div className="font-semibold text-[hsl(var(--foreground))]">
          {user.firstName} {user.lastName}
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (user: User) => (
        <span className="text-[hsl(var(--muted-foreground))]">{user.email}</span>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: User) => (
        <span className="px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full bg-blue-100 text-[hsl(var(--foreground))] font-semibold dark:bg-blue-900/50 ring-1 ring-blue-200 dark:ring-blue-800">
          {user.role}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: User) => (
        <span
          className={cn(
            'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
            user.isActive
              ? 'bg-emerald-100 text-[hsl(var(--foreground))] font-semibold dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800'
              : 'bg-red-100 text-[hsl(var(--foreground))] font-semibold dark:bg-red-900/50 ring-1 ring-red-200 dark:ring-red-800'
          )}
        >
          {user.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      render: (user: User) => (
        <span className="text-[hsl(var(--muted-foreground))]">
          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
  ];

  const logColumns = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      render: (log: any) => (
        <div>
          <div className="text-sm font-medium text-[hsl(var(--foreground))]">
            {new Date(log.timestamp).toLocaleDateString()}
          </div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            {new Date(log.timestamp).toLocaleTimeString()}
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (log: any) => (
        <span className="px-2 py-1 inline-flex text-xs leading-5 font-bold rounded bg-blue-100 text-[hsl(var(--foreground))] font-semibold dark:bg-blue-900/50">
          {log.action}
        </span>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (log: any) => (
        <div className="max-w-md truncate text-sm text-[hsl(var(--muted-foreground))]" title={log.details}>
          {log.details || '-'}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (log: any) => (
        <span
          className={cn(
            'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
            log.status === 'success'
              ? 'bg-emerald-100 text-[hsl(var(--foreground))] font-semibold dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800'
              : log.status === 'failed'
                ? 'bg-red-100 text-[hsl(var(--foreground))] font-semibold dark:bg-red-900/50 ring-1 ring-red-200 dark:ring-red-800'
                : 'bg-amber-100 text-[hsl(var(--foreground))] font-semibold dark:bg-amber-900/50 ring-1 ring-amber-200 dark:ring-amber-800'
          )}
        >
          {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <button
              onClick={() => navigate('/organizations')}
              className="p-2 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors shrink-0"
            >
              <MdArrowBack className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-4xl font-bold text-[hsl(var(--foreground))] mb-2 bg-[#0088cc] hover:bg-[#0077b3] transition-all bg-clip-text text-transparent">
                {organization.name}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={cn(
                    'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
                    organization.type === 'customer'
                      ? 'bg-blue-100 text-[hsl(var(--foreground))] font-semibold dark:bg-blue-900/50 ring-1 ring-blue-200 dark:ring-blue-800'
                      : 'bg-purple-100 text-[hsl(var(--foreground))] font-semibold dark:bg-purple-900/50 ring-1 ring-purple-200 dark:ring-purple-800'
                  )}
                >
                  {organization.type === 'customer' ? 'Customer' 
                  : organization.type === 'internal' ? 'In-house Vendor' : 'Vendor'}
                </span>
                <span
                  className={cn(
                    'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
                    organization.isActive
                      ? 'bg-emerald-100 text-[hsl(var(--foreground))] font-semibold dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800'
                      : 'bg-red-100 text-[hsl(var(--foreground))] font-semibold dark:bg-red-900/50 ring-1 ring-red-200 dark:ring-red-800'
                  )}
                >
                  {organization.isActive ? 'Active' : 'Inactive'}
                </span>
                {organization.createdAt && (
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">
                    Created {new Date(organization.createdAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          {isVendorOrg && (
            <button
              type="button"
              onClick={handleSendRfqToVendor}
              disabled={!canSendRfq}
              title={
                !vendorOrganizationId
                  ? 'Organization ID is missing'
                  : !organization.isActive
                    ? 'Activate vendor before sending RFQ'
                    : 'Create RFQ for this vendor'
              }
              className={cn(
                'flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none shrink-0',
                canSendRfq
                  ? 'bg-[#0088cc] hover:bg-[#0077b3] text-white hover:shadow-[#0088cc]/25'
                  : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'
              )}
            >
              <MdSend className="w-5 h-5" />
              Send RFQ
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[hsl(var(--border))]">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  'flex items-center gap-2 py-4 px-1 border-b-2 font-semibold text-sm transition-colors',
                  isActive
                    ? 'border-[#0088cc] text-[hsl(var(--primary))]'
                    : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[#0088cc]/25 dark:text-[#0088cc]/65 dark:hover:text-[#0088cc]/45'
                )}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6 rounded-2xl bg-[hsl(var(--card))]/80 backdrop-blur-xl border border-[hsl(var(--border))]/50 shadow-lg">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-3 mb-2">
                  <MdPeople className="w-6 h-6 text-[hsl(var(--primary))]" />
                  <h3 className="text-lg font-semibold text-[hsl(var(--foreground))]">Users</h3>
                </div>
                <p className="text-3xl font-bold text-[hsl(var(--primary))]">{users?.length || 0}</p>
              </div>
              {organization.type === 'customer' && (
                <>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/20 dark:to-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-3 mb-2">
                      <MdDirectionsBoat className="w-6 h-6 text-[hsl(var(--foreground))] font-semibold" />
                      <h3 className="text-lg font-semibold text-[hsl(var(--foreground))]">Vessels</h3>
                    </div>
                    <p className="text-3xl font-bold text-[hsl(var(--foreground))] font-semibold">{vessels?.length || 0}</p>
                  </div>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-3 mb-2">
                      <MdBusiness className="w-6 h-6 text-[hsl(var(--foreground))] font-semibold" />
                      <h3 className="text-lg font-semibold text-[hsl(var(--foreground))]">Business Units</h3>
                    </div>
                    <p className="text-3xl font-bold text-[hsl(var(--foreground))] font-semibold">{businessUnits?.length || 0}</p>
                  </div>
                </>
              )}
              {isVendorOrg && (
                <>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/20 dark:to-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-3 mb-2">
                      <MdCategory className="w-6 h-6 text-[hsl(var(--foreground))] font-semibold" />
                      <h3 className="text-lg font-semibold text-[hsl(var(--foreground))]">Catalog items</h3>
                    </div>
                    <p className="text-3xl font-bold text-[hsl(var(--foreground))] font-semibold">
                      {catalogLoading ? '…' : vendorCatalog?.length ?? 0}
                    </p>
                  </div>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-3 mb-2">
                      <MdInventory2 className="w-6 h-6 text-[hsl(var(--foreground))] font-semibold" />
                      <h3 className="text-lg font-semibold text-[hsl(var(--foreground))]">Inventory SKUs</h3>
                    </div>
                    <p className="text-3xl font-bold text-[hsl(var(--foreground))] font-semibold">
                      {inventoryLoading ? '…' : vendorInventoryResult?.total ?? vendorInventory.length}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Onboarding & Billing Details */}
            {(organization as any).metadata?.onboardingDetails && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  <h3 className="text-lg font-bold text-[hsl(var(--foreground))] mb-4 flex items-center gap-2">
                    <MdBusinessCenter className="text-blue-600" />
                    Banking & Billing
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Bank Name</p>
                        <p className="text-sm font-medium">{(organization as any).metadata.onboardingDetails.banking?.bankName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Account Name</p>
                        <p className="text-sm font-medium">{(organization as any).metadata.onboardingDetails.banking?.accountName || 'N/A'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">IBAN / Account</p>
                      <p className="text-sm font-mono font-medium">{(organization as any).metadata.onboardingDetails.banking?.iban || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Billing Email</p>
                      <p className="text-sm font-medium">{(organization as any).metadata.onboardingDetails.billing?.invoiceEmail || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  <h3 className="text-lg font-bold text-[hsl(var(--foreground))] mb-4 flex items-center gap-2">
                    <MdBusiness className="text-blue-600" />
                    Organization Address
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Primary Address</p>
                      <p className="text-sm font-medium">
                        {(organization as any).metadata.onboardingDetails.address?.address1}<br />
                        {(organization as any).metadata.onboardingDetails.address?.city}, {(organization as any).metadata.onboardingDetails.address?.province}<br />
                        {(organization as any).metadata.onboardingDetails.address?.country} – {(organization as any).metadata.onboardingDetails.address?.postalCode}
                      </p>
                    </div>
                    <div className="pt-2 border-t border-[hsl(var(--border))]">
                      <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Tax ID / VAT</p>
                      <p className="text-sm font-medium">{(organization as any).metadata.onboardingDetails.taxId || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {license && (
              <div className="p-6 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/20 dark:to-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-3 mb-4">
                  <MdVpnKey className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  <h3 className="text-lg font-semibold text-[hsl(var(--foreground))]">License Status</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Status</p>
                    <p className="text-lg font-bold text-[hsl(var(--foreground))] capitalize">{license.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Expires</p>
                    <p className="text-lg font-bold text-[hsl(var(--foreground))]">
                      {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  {license.usageLimits?.users && (
                    <div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Users Limit</p>
                      <p className="text-lg font-bold text-[hsl(var(--foreground))]">
                        {license.currentUsage?.users || 0} / {license.usageLimits.users}
                      </p>
                    </div>
                  )}
                  {organization.type === 'customer' && license.usageLimits?.vessels && (
                    <div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Vessels Limit</p>
                      <p className="text-lg font-bold text-[hsl(var(--foreground))]">
                        {license.currentUsage?.vessels || 0} / {license.usageLimits.vessels}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            {usersLoading ? (
              <PageSkeleton variant="panel" />
            ) : (
              <DataTable
                columns={userColumns}
                data={users || []}
                emptyMessage="No users found for this organization."
              />
            )}
          </div>
        )}

        {activeTab === 'license' && (
          <div>
            {licenseLoading ? (
              <PageSkeleton variant="panel" />
            ) : license ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] mb-2">License Status</h3>
                    <p className="text-xl font-bold text-[hsl(var(--foreground))] capitalize">{license.status}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] mb-2">Expires At</h3>
                    <p className="text-xl font-bold text-[hsl(var(--foreground))]">
                      {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                {license.usageLimits && (
                  <div>
                    <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-4">Usage Limits</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(license.usageLimits).map(([key, limit]) => (
                        <div key={key} className="p-4 rounded-lg bg-[hsl(var(--secondary))]">
                          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1 capitalize">{key}</p>
                          <p className="text-xl font-bold text-[hsl(var(--foreground))]">
                            {license.currentUsage?.[key as keyof typeof license.currentUsage] || 0} / {limit}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[hsl(var(--muted-foreground))]">No license found for this organization.</p>
            )}
          </div>
        )}

        {activeTab === 'vessels' && organization.type === 'customer' && (
          <div>
            {vesselsLoading ? (
              <PageSkeleton variant="panel" />
            ) : (
              <p className="text-[hsl(var(--muted-foreground))]">
                Vessel management coming soon. API endpoint needs to be implemented.
              </p>
            )}
          </div>
        )}

        {activeTab === 'business-units' && organization.type === 'customer' && (
          <div>
            {busLoading ? (
              <PageSkeleton variant="panel" />
            ) : (
              <p className="text-[hsl(var(--muted-foreground))]">
                Business unit management coming soon. API endpoint needs to be implemented.
              </p>
            )}
          </div>
        )}

        {activeTab === 'catalog' && isVendorOrg && (
          <div>
            {catalogLoading ? (
              <PageSkeleton variant="panel" />
            ) : (
              <DataTable
                columns={catalogColumns}
                data={vendorCatalog || []}
                emptyMessage="No catalog items listed for this vendor yet."
              />
            )}
          </div>
        )}

        {activeTab === 'inventory' && isVendorOrg && (
          <div>
            {inventoryLoading ? (
              <PageSkeleton variant="panel" />
            ) : (
              <DataTable
                columns={inventoryColumns}
                data={vendorInventory}
                emptyMessage="No inventory stock records for this vendor yet."
              />
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div>
            {logsLoading ? (
              <PageSkeleton variant="panel" />
            ) : (
              <DataTable
                columns={logColumns}
                data={logsResult?.logs || []}
                emptyMessage="No activity logs found for this organization."
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

